/* eslint-disable react/jsx-props-no-spreading */
import { capitalize } from 'lodash/string'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { monthDayYear } from '../../../../utils/dateTools'
import { formatAsCurrencyOrZero } from '../../../../utils/priceTools'
import Link from '../../../global/Link'
import Table from '../../../Table'

const LinkToInvoice = ({ invoiceId, value }) => (
  <Link to={`/account/transactions/invoices/${invoiceId}`}>{value}</Link>
)

LinkToInvoice.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
}

const TransactionCell = ({ row, value }) => {
  if (row.original.type === 'invoice') {
    return <LinkToInvoice invoiceId={row.original.id} value={value} />
  }
  return value
}

TransactionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string,
    }).isRequired,
  }),
  value: PropTypes.string.isRequired,
}

const TransactionsTable = ({ className, transactions }) => {
  const data = useMemo(() => transactions, [transactions])

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: (transaction) => monthDayYear(transaction.date),
        disableSortBy: false,
        id: 'date',
        sortType: (a, b) => {
          if (a.original.date > b.original.date) return 1
          return -1
        },
        Cell: TransactionCell,
      },
      {
        Header: 'Transaction',
        accessor: (transaction) =>
          `${capitalize(transaction.type)} #${transaction.transactionNumber}`,
        disableSortBy: true,
        Cell: TransactionCell,
      },
      {
        id: 'charge',
        Header: 'Charge',
        accessor: (transaction) =>
          transaction.charge != null ? formatAsCurrencyOrZero({ amount: transaction.charge }) : '–',
        disableSortBy: true,
        Cell: TransactionCell,
      },
      {
        Header: 'Payment',
        accessor: (transaction) =>
          transaction.payment != null
            ? formatAsCurrencyOrZero({ amount: transaction.payment })
            : '–',
        disableSortBy: true,
        Cell: TransactionCell,
      },
    ],
    [],
  )

  const initialSortBy = useMemo(
    () => [
      {
        desc: true,
        id: 'date',
      },
    ],
    [],
  )

  return <Table className={className} columns={columns} data={data} initialSortBy={initialSortBy} />
}

TransactionsTable.defaultProps = {
  className: '',
  transactions: null,
}

TransactionsTable.propTypes = {
  className: PropTypes.string,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      transactionNumber: PropTypes.string,
      date: PropTypes.string,
      payment: PropTypes.number,
      charge: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
}

const StyledTransactionTable = styled(TransactionsTable)(({ theme }) => ({
  'tbody tr:hover': {
    backgroundColor: theme.colors.coolGray.cool100,
  },
  td: {
    padding: '0px',
  },
  a: {
    textDecoration: 'none',
    padding: '20px 4px 20px 4px',
    display: 'block',
    width: '100%',
  },
}))

export default StyledTransactionTable
